import { useState } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Icon } from "@/lib/icons";
import type { ExcelFileInfo } from "@/api/word.api";
import { ExcelFilePreviewDialog } from "./ExcelFilePreviewDialog";
import { ExcelFilePhrasesDialog } from "./ExcelFilePhrasesDialog";

interface ExcelFileRowProps {
  file: ExcelFileInfo;
  labels: {
    phrases: string;
    fileUnknown: string;
    updateFile: string;
    excelFile: string;
    updating: string;
    update: string;
    deleteFileTitle: string;
    deleteFileDescription: string;
    delete: string;
    cancel: string;
    status: string;
    pending: string;
    approved: string;
    rejected: string;
    approve: string;
    reject: string;
    rejectPrompt: string;
    preview: string;
    previewLoading: string;
    previewError: string;
    previewEmpty: string;
    previewSummary: string;
  };
  isUpdateDialogOpen: boolean;
  updatingFileId: number | null;
  selectedFile: File | null;
  isDownloading: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  onDownload: (id: number) => void;
  onOpenUpdate: (id: number) => void;
  onCloseUpdate: () => void;
  onFileSelect: (file: File | null) => void;
  onUpdateSubmit: (e: React.FormEvent) => void;
  onDelete: (id: number) => void;
  isSuperAdmin: boolean;
  isApproving: boolean;
  isRejecting: boolean;
  onApprove: (id: number) => void;
  onReject: (id: number, reason: string) => void;
}

function extractBlobName(url: string) {
  try {
    return new URL(url).pathname.split("/").pop() ?? url;
  } catch {
    return url.split("/").pop() ?? url;
  }
}

export function ExcelFileRow({
  file,
  labels,
  isUpdateDialogOpen,
  updatingFileId,
  selectedFile,
  isDownloading,
  isUpdating,
  isDeleting,
  onDownload,
  onOpenUpdate,
  onCloseUpdate,
  onFileSelect,
  onUpdateSubmit,
  onDelete,
  isSuperAdmin,
  isApproving,
  isRejecting,
  onApprove,
  onReject,
}: ExcelFileRowProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPhrasesOpen, setIsPhrasesOpen] = useState(false);

  return (
    <article className="group col-span-full grid grid-cols-subgrid border-b border-border/40 py-5 last:border-b-0">
      <div className="col-span-full grid grid-cols-subgrid items-center">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-foreground">{extractBlobName(file.fileUrl)}</h3>
        </div>

        <div>
          <span className="inline-flex items-center rounded-full bg-muted/30 px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-foreground/80">
            {file.lang}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
            {file.user?.username?.[0]?.toUpperCase() || "?"}
          </div>
          <span className="text-sm text-muted-foreground">
            {file.user?.username || labels.fileUnknown}
          </span>
        </div>

        <span className="text-xs text-muted-foreground">
          {file.createdAt ? format(new Date(file.createdAt), "dd MMM yyyy · HH:mm") : "—"}
        </span>

        <div className="flex items-center gap-2">
          <span className={`text-[0.62rem] font-semibold uppercase tracking-[0.14em] ${file.approvalStatus === "APPROVED" ? "text-success dark:text-success" : file.approvalStatus === "REJECTED" ? "text-danger dark:text-danger" : "text-brand dark:text-brand"}`}>
            {file.approvalStatus === "APPROVED" ? labels.approved : file.approvalStatus === "REJECTED" ? labels.rejected : labels.pending}
          </span>
        </div>

        <div className="flex items-center gap-3 justify-end">
          {isSuperAdmin && file.approvalStatus === "PENDING" && (
            <>
              <button type="button" disabled={isApproving} onClick={() => onApprove(file.id)} className="text-xs font-semibold text-success hover:text-success disabled:opacity-40">{labels.approve}</button>
              <button type="button" disabled={isRejecting} onClick={() => { const reason = window.prompt(labels.rejectPrompt); if (reason?.trim()) onReject(file.id, reason); }} className="text-xs font-semibold text-danger hover:text-danger disabled:opacity-40">{labels.reject}</button>
            </>
          )}

          {file.approvalStatus === "APPROVED" && (
            <>
              <button
                type="button"
                onClick={() => setIsPhrasesOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground/80 transition-colors hover:text-foreground"
              >
                <Icon name="edit" className="h-3.5 w-3.5" />
                {labels.phrases}
              </button>
              <ExcelFilePhrasesDialog
                fileId={file.id}
                fileName={extractBlobName(file.fileUrl)}
                open={isPhrasesOpen}
                onOpenChange={setIsPhrasesOpen}
              />
            </>
          )}

          <button
            type="button"
            aria-label={labels.preview}
            onClick={() => setIsPreviewOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            <Icon name="eye" className="h-3.5 w-3.5" />
          </button>

          <ExcelFilePreviewDialog
            fileId={file.id}
            fileName={extractBlobName(file.fileUrl)}
            open={isPreviewOpen}
            onOpenChange={setIsPreviewOpen}
            labels={{ previewLoading: labels.previewLoading, previewError: labels.previewError, previewEmpty: labels.previewEmpty, previewSummary: labels.previewSummary }}
          />

          <button
            type="button"
            disabled={isDownloading}
            onClick={() => onDownload(file.id)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
          >
            <Icon name="download" className="h-3.5 w-3.5" />
          </button>

          <Dialog
            open={isUpdateDialogOpen && updatingFileId === file.id}
            onOpenChange={(open) => {
              if (open) onOpenUpdate(file.id);
              else onCloseUpdate();
            }}
          >
            <DialogTrigger asChild>
              <button
                type="button"
                aria-label={labels.updateFile}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                <Icon name="upload" className="h-3.5 w-3.5" />
              </button>
            </DialogTrigger>
            <DialogContent className="border-border/40 bg-card">
              <DialogHeader>
                <DialogTitle className="text-foreground">{labels.updateFile}</DialogTitle>
              </DialogHeader>
              <form onSubmit={onUpdateSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="file" className="text-muted-foreground">
                    {labels.excelFile}
                  </Label>
                  <Input
                    id="file"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => onFileSelect(e.target.files?.[0] || null)}
                    className="border-border/40 bg-muted/20 text-foreground/80"
                  />
                </div>
                <DialogFooter>
                  <button
                    type="submit"
                    disabled={!selectedFile || isUpdating}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand transition-colors hover:text-brand disabled:cursor-not-allowed disabled:opacity-35"
                  >
                    {isUpdating ? labels.updating : labels.update}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                disabled={isDeleting}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-danger transition-colors hover:text-danger disabled:cursor-not-allowed disabled:opacity-35"
              >
                <Icon name="trash2" className="h-3.5 w-3.5" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="border-border/40 bg-card">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-foreground">{labels.deleteFileTitle}</AlertDialogTitle>
                <AlertDialogDescription className="text-muted-foreground">
                  {labels.deleteFileDescription}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-border/40 bg-transparent text-muted-foreground hover:bg-muted/20">
                  {labels.cancel}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(file.id)}
                  className="bg-danger/20 text-danger hover:bg-danger/30"
                >
                  {labels.delete}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </article>
  );
}
